import React, { cloneElement, useEffect, useState } from "react"
import clsx from "clsx"
import {
	ListItem,
	ListItemAvatar,
	Badge,
	Grid,
	ListItemText,
	Typography,
	Avatar,
	Box,
	Popover,
	List,
	Fab,
	Divider as MaterialDivider,
	Tooltip,
	ListItemSecondaryAction
} from "@material-ui/core"
import {
	PersonOutline as AttendantIcon,
	ExpandMore as ChatOptionsIcon,
	Snooze as SnoozeIcon,
	ShoppingCart
} from "@material-ui/icons"

import { Divider, PopConfirm } from "@/components"

import {
	SECOND_IN_MS,
	formatMessageDateLabel,
	formatDateInHours
} from "@/utils/time"
import { channelInfo } from "@/utils/channel"
import { keepOnlyDigits } from "@/utils/phone"
import { convertRGBAObjectToString } from "@/utils/color"
import { getChatMessageSketch, buildChatLink } from "@/utils/chat"
import { getPhoneNumber, isLidContact } from "@/utils/contact"

import { IMessage } from "@/protocols/channel"

import MessageContentPlaceholder from "@/pages/Attendance/Chat/ConversationPanel/MessageList/MessageContentPlaceholder"
import CounterChip from "@/pages/Attendance/Chat/ChatListPanel/CounterChip"
import MessageStatusIcon from "@/pages/Attendance/Chat/ConversationPanel/MessageList/MessageStatusIcon"
import MessageDeletedContent from "@/pages/Attendance/Chat/ConversationPanel/MessageList/MessageDeletedContent"
import ChatAttendanceStatusChip from "@/pages/Attendance/Chat/ChatListPanel/ChatAttendanceStatusChip"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import useSocket from "@/hooks/useSocket"
import useCustomMemo from "@/hooks/useCustomMemo"
import useContactDisplay from "@/hooks/useContactDisplay"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"
import { colors } from "@/styles/colors"
import useStyles from "@/pages/Attendance/Chat/ChatListPanel/ChatList/ChatListItem/styles"
import useCustomStyles from "@/styles/custom"
import WABAChatCommunicationPermissionTooltip from "@/@integrations/WABA/components/Chat/WABAChatCommunicationPermissionTooltip"
import { useMetaFreeEntryPointWindow } from "@/@integrations/WABA/hooks/useMetaFreeEntryPointWindow"
import { MetaFreeEntryPointWindowIcon } from "@/assets/icons"
import LidContactWarning from "@/components/LidContactWarning"

import ChatGroupPermissionGate from "@/components/PermissionGate/ChatGroupPermissionGate"
import useBasePermissionGateStyles from "@/components/PermissionGate/BasePermissionGate/styles"

type ChatListItemProps = {
	chatId: number
	onClick: () => void
	showChatAttendanceStatus?: boolean
}

type IconsStatusChatListItem = "snooze" | "unreadMessages" | "attendanceStatus"

const ChatListItem = (props: ChatListItemProps) => {
	const {
		chatId,
		onClick,
		showChatAttendanceStatus
	} = props

	const classes = useStyles()
	const customClasses = useCustomStyles()
	const basePermissionGateClasses = useBasePermissionGateStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()
	const socket = useSocket()
	const { checkChatPermission } = usePermissionStateStore()
	const { getDisplayName } = useContactDisplay()

	const chat = chatGlobalStateStore.chat.getById(chatId)
	const client = chatGlobalStateStore.client.getById(chat.client.id)
	const clientTags = client?.tagIds?.map(tagId => chatGlobalStateStore.tag.getById(tagId))?.filter(tag => tag) || []

	const active = chat.id === chatGlobalStateStore.chat.currentOpenedId
	const clientPhoneNumber = getPhoneNumber(client)
	const title = getDisplayName(client?.nickname, clientPhoneNumber)
	const titleName = getDisplayName(client?.name, clientPhoneNumber)
	const isLid = isLidContact(client)
	const unreadMessagesCount = chat.unreadMessagesCount
	const markedUnread = !!chat.consolidatedData.markedUnread
	const channelType = chat.channelType
	const pictureUrl = client?.picture_url
	const lastMessage = chat.lastMessage as IMessage
	const attendantName = chat.attendance?.userName
	const chatStatus = chat.status
	const isAwakeningSnooze = chat.extraData?.awakeningSnooze

	const isWabaChatChannel = channelType === "waba"

	const { canShowExpiringWarning: isMetaFreeEntryPointWindowExpiring } = useMetaFreeEntryPointWindow({
		status: chat.wabaChannelChatFreeEntryPointWindow?.status ?? "paused",
		scheduledPauseAt: chat.wabaChannelChatFreeEntryPointWindow?.scheduledPauseAt
	})

	const [chatOptionsRef, setChatOptionsRef] = useState<HTMLElement | null>(null)
	const [optionsIconVisible, setOptionsIconVisible] = useState<boolean>(false)
	const [showMessageSketch, setShowMessageSketch] = useState<boolean>(false)

	const handleShowOptionsIcon = () => {
		setOptionsIconVisible(true)
	}

	const handleHideOptionsIcon = () => {
		setOptionsIconVisible(false)
	}

	const handleOpenChatOptionsPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setChatOptionsRef(event.currentTarget)
	}

	const handleCloseChatOptionsPopover = () => {
		setChatOptionsRef(null)
		handleHideOptionsIcon()
	}

	const handleShowMessageSketch = () => {
		const messageSketch = getChatMessageSketch(chatId)
		const isCurrentChat = chat.id === chatGlobalStateStore.chat.current?.id

		const isShowMessageSketch = !!messageSketch && !isCurrentChat

		setShowMessageSketch(isShowMessageSketch)
	}

	const getChatDescription = () => {
		if (!lastMessage) {
			return "..."
		}

		if (lastMessage.extraData?.catalogueOrderMessage) {
			return <>
				<ShoppingCart />
				<Typography
					className={`${classes.chatDescription} ${customClasses.inlineText}`}
					{...({
						variant: "caption",
						color: "textPrimary"
					})}>
					Itens do catálogo
				</Typography>
			</>
		}
		const messageType = lastMessage.type

		const isNotification = messageType?.includes("notification")
		const isRawText = messageType === "text"
		const isCustomLink = messageType === "custom-link"
		const isCiphertext = messageType === "ciphertext"
		const wasMessageSentByCustomer = lastMessage.sentByCustomer

		if (isCiphertext) {
			return <>
				<Typography
					className={`${classes.chatDescription} ${customClasses.inlineText}`}
					{...({
						variant: "caption",
						color: "textPrimary"
					})}>
					Aguardando essa mensagem.
					Isso pode demorar um pouco
				</Typography>
			</>
		}

		if (lastMessage.deletedAt) {
			return (
				<MessageDeletedContent
					sentByCustomer={lastMessage.sentByCustomer}
				/>
			)
		}

		return (
			<>
				{showMessageSketch ? (
					<>
						<Box
							component={Typography}
							title={getChatMessageSketch(chatId) as string}
							className={`${classes.chatSketch} ${customClasses.inlineText}`}
							{...({
								variant: "caption",
								color: "textPrimary"
							})}
						>
							Rascunho: <span className={classes.chatDescription}>{getChatMessageSketch(chatId)}</span>
						</Box>
					</>
				) : (
					<>
						{wasMessageSentByCustomer && (
							<>
								<MessageStatusIcon
									className={classes.messageStatus}
									status={lastMessage.status}
								/>

								<Divider orientation="vertical" size={0.5} />
							</>
						)}

						{(isNotification || isRawText || isCustomLink) ? (
							<Box
								component={Typography}
								title={lastMessage?.content}
								className={`${classes.chatDescription} ${customClasses.inlineText}`}
								{...({
									variant: "caption",
									color: "textPrimary"
								})}
							>
								{lastMessage?.content}
							</Box>
						) : (
							<MessageContentPlaceholder
								type={messageType}
								caption={lastMessage?.caption}
								className={classes.chatDescriptionContainer}
								extraData={lastMessage?.extraData}
								content={lastMessage?.content}
							/>
						)}
					</>
				)}
			</>
		)
	}

	const setChatMarkedUnread = async (chatId: number, unread: boolean) => {
		chatGlobalStateStore.chat.updateById(chatId, {
			consolidatedData: {
				markedUnread: unread
			}
		})
	}

	const handleSetChatUnread = async () => {
		setChatMarkedUnread(chatId, true)

		try {
			await socket.setChatUnread({
				inboxChannelChatId: chatId,
				channelType
			})
		} catch (error) {
			setChatMarkedUnread(chatId, false)
		}

		handleCloseChatOptionsPopover()
		handleHideOptionsIcon()
	}

	const handleFinishAttendance = async () => {
		await chatGlobalStateStore.attendance.finishByChatId(chat.id)
	}

	const deleteChat = async () => {
		await chatGlobalStateStore.chat.deleteById(chat.id)
	}

	const handleDeleteChat = async () => {
		const props = {
			title: "Deseja remover chat?",
			confirmButtonText: "REMOVER",
			onConfirm: () => deleteChat()
		}
		PopConfirm.open(props)
	}

	const handleOpenChatInNewTab = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation()
		window.open(buildChatLink(chat.client.id), "_blank")
		handleCloseChatOptionsPopover()
	}

	const counterChipColor = (chatStatus === "on-going" || chatStatus === "snooze" || markedUnread) ? (
		colors.green[500]
	) : (
		colors.gray[300]
	)

	const showOptionsButton = !!optionsIconVisible
	const showTitleName = (keepOnlyDigits(title) === clientPhoneNumber) && (chat?.type !== "group")

	const chatAccessRules = chatGlobalStateStore.chat.userHasAccessToChatRules(chat)
	const hasChatAccess = chatAccessRules.alwaysAllowed || chatAccessRules.requiredActions.some(action => checkChatPermission(action))

	const renderChatStatusIcons = () => {
		const icons: Record<IconsStatusChatListItem, React.ReactElement> = {
			snooze: (
				<SnoozeIcon className={isAwakeningSnooze ? classes.snoozeAwakening : classes.snoozeDefined} fontSize="small" />

			),
			unreadMessages: (
				<CounterChip
					active={unreadMessagesCount > 0 || markedUnread}
					counter={unreadMessagesCount > 0 ? unreadMessagesCount : -1}
					backgroundColor={counterChipColor}
					fontColor={colors.gray[0]}
					size="medium"
				/>
			),
			attendanceStatus: (
				<ChatAttendanceStatusChip
					chatStatus={chatStatus}
					backgroundColor={colors.gray[300]}
					fontColor={colors.gray[0]}
					size={24}
				/>
			)
		}

		return (
			<Grid container justifyContent="flex-end" alignItems="center">

				{showChatAttendanceStatus && icons.attendanceStatus}

				{!showChatAttendanceStatus && (chatStatus === "snooze" || isAwakeningSnooze) ? icons.snooze : null}

				{(!showChatAttendanceStatus && unreadMessagesCount > 0) || markedUnread ? icons.unreadMessages : null}

			</Grid>
		)
	}

	const renderChatListItem = () => {
		return (
			<ListItem
				button
				onClick={hasChatAccess && onClick}
				className={`${active ? `${classes.selectedChatListItem}` : ""} ${classes.chatListItem}`}
				onMouseEnter={() => handleShowOptionsIcon()}
				onMouseLeave={() => handleHideOptionsIcon()}
			>
				<ListItemAvatar>
					<Badge
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						badgeContent={(
							<Grid
								container
								className={classes.badgeContainer}
							>
								{channelInfo[channelType]?.icon || null}
							</Grid>
						)}
					>
						<Avatar src={pictureUrl} />
					</Badge>
				</ListItemAvatar>

				<ListItemText
					primary={(
						<Grid container direction="row" alignItems="center">
							{
								isWabaChatChannel && (
									<Grid item style={{ marginRight: 4 }}>
										<WABAChatCommunicationPermissionTooltip status={chat.wabaChannelChatCommunicationPermission?.status ?? "paused"} scheduledPauseAt={chat.wabaChannelChatCommunicationPermission?.scheduledPauseAt} />
									</Grid>
								)
							}
							<Tooltip
								title={`${title}${showTitleName ? ` ~${titleName}` : ""}`}
								enterDelay={1 * SECOND_IN_MS}
							>
								<Typography
									variant="h2"
									color="textPrimary"
									className={classes.chatTitle}
								>
									{title}
									{showTitleName && (
										<Typography
											variant="caption"
											color="textPrimary"
											className={classes.chatTitleName}
										>
											~{titleName}
										</Typography>
									)}
								</Typography>
							</Tooltip>
							{isWabaChatChannel && chat.wabaChannelChatFreeEntryPointWindow && chat.wabaChannelChatFreeEntryPointWindow.status !== "paused" && (
								<Grid item>
									<MetaFreeEntryPointWindowIcon className={clsx(classes.metaFreeEntryPointWindowIcon, {
										[classes.metaFreeEntryPointWindowIconExpiring]: isMetaFreeEntryPointWindowExpiring
									})} />
								</Grid>
							)}
							{isLid && (
								<Grid item style={{ marginLeft: 4 }}>
									<LidContactWarning />
								</Grid>
							)}
						</Grid>
					)}
					secondary={(
						<Grid
							container
							direction="column"
						>
							<Grid
								container
								alignItems="center"
								className={classes.chatDescriptionContainer}
							>
								{getChatDescription()}
							</Grid>

							<Grid
								container
								alignItems="center"
							>
								<AttendantIcon
									className={classes.chatAttendantIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								<Tooltip
									title={attendantName || "Sem atendente"}
									enterDelay={1 * SECOND_IN_MS}
								>
									<Typography
										className={classes.chatAttendantName}
									>
										{attendantName || "Sem atendente"}
									</Typography>
								</Tooltip>

								<Divider orientation="vertical" size={0.5} />

								{clientTags.map(tag => (
									<>
										<Grid
											style={{
												backgroundColor: convertRGBAObjectToString(tag.color),
												color: colors.gray[50]
											}}
											className={classes.chatClientTag}
										/>

										<Divider orientation="vertical" size={0.2} />
									</>
								))}
							</Grid>
						</Grid>
					)}
				/>

				<ListItemSecondaryAction
					onMouseEnter={() => handleShowOptionsIcon()}
					onMouseLeave={() => handleHideOptionsIcon()}
				>
					<Grid
						container
						direction="column"
						alignItems="flex-end"
						justifyContent="center"
						className={classes.chatInfoContainer}
					>
						{lastMessage ? (
							<>
								<Grid item>
									<Typography
										variant="caption"
										className={classes.chatUnreadMessagesText}
										color="textPrimary"
									>
										{formatMessageDateLabel(new Date(lastMessage.createdAt))}
									</Typography>
								</Grid>
								<Grid item>
									<Typography
										variant="caption"
										className={classes.chatUnreadMessagesText}
										color="textPrimary"
									>
										{formatDateInHours(new Date(lastMessage.createdAt))}
									</Typography>
								</Grid>
							</>
						) : (
							<Typography
								variant="caption"
								className={classes.chatUnreadMessagesText}
								color="textPrimary"
							>
								{""}
							</Typography>
						)}

						{renderChatStatusIcons()}

						{hasChatAccess && (
							<>
								<Fab
									onClick={(event) => handleOpenChatOptionsPopover(event)}
									className={classes.chatOptionsButton}
								>
									{
										showOptionsButton && <ChatOptionsIcon />
									}
								</Fab>

								<Popover
									open={!!chatOptionsRef}
									anchorEl={chatOptionsRef}
									onClose={handleCloseChatOptionsPopover}
									anchorOrigin={{ vertical: "top", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}
								>
									<List>
										<ListItem
											button
											onClick={handleSetChatUnread}
										>
											<ListItemText primary="Marcar como não lido" />
										</ListItem>

										<ListItem
											button
											onClick={handleOpenChatInNewTab}
										>
											<ListItemText primary="Abrir em nova aba" />
										</ListItem>

										{chatStatus === "on-going" && (
											<ListItem
												button
												onClick={handleFinishAttendance}
											>
												<ListItemText primary="Finalizar atendimento" />
											</ListItem>
										)}

										<ChatGroupPermissionGate action="delete-chat">
											<ListItem
												button
												onClick={handleDeleteChat}
											>
												<ListItemText primary="Remover chat" />
											</ListItem>
										</ChatGroupPermissionGate>
									</List>
								</Popover>
							</>
						)}
					</Grid>
				</ListItemSecondaryAction>
			</ListItem>
		)
	}

	const renderChatListItemWithAccessGate = () => {
		const chatListItemElement = renderChatListItem()

		if (hasChatAccess) {
			return chatListItemElement
		}

		return (
			<Tooltip title={PERMISSION_DENIED_MESSAGE} arrow>
				<Box
					component="span"
					className={basePermissionGateClasses.disabledBox}
					onClick={(event) => event.stopPropagation()}
				>
					{cloneElement(chatListItemElement, { disabled: true })}
				</Box>
			</Tooltip>
		)
	}

	useEffect(() => {
		handleShowMessageSketch()
	}, [chatGlobalStateStore.chat.current?.id])

	return useCustomMemo(() => (
		<Grid
			direction="column"
			className={classes.chatListItemContainer}
			container
		>
			{renderChatListItemWithAccessGate()}
			<MaterialDivider variant="inset" component="li" />
		</Grid>
	), [
		active,
		hasChatAccess,
		unreadMessagesCount,
		JSON.stringify(lastMessage),
		title,
		lastMessage?.status,
		optionsIconVisible,
		chatOptionsRef,
		lastMessage?.deletedAt,
		pictureUrl,
		JSON.stringify(client?.tagIds),
		showMessageSketch,
		markedUnread,
		chat.wabaChannelChatFreeEntryPointWindow?.status
	])
}

export default ChatListItem
